import * as moment from "moment";
import { useUserIdState } from "../../../selectors/userInfoSelectors";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "../../../selectors/enviromentSelectors";
import PersesSearchAPI from "../../../services/persesSearchService";
import { QUERY_STATUS } from "../../../utils/constants/dashboard/dashboardModuleConstants";
import useDashboardCalendarFilter from "../../../hooks/useDashboardCalendarFilter";
import activitiesStatus from "../../../utils/constants/activities/activitiesStatus";
import useCalendarActivityTypes from "../../../hooks/useCalendarActivityTypes";
import { CalendarActivityTypes } from "@customTypes/filter";
import { LateActivitiesCounterResponse } from "@customTypes/services/responses";

export const useCalendarFilterRedirect = () => {
  const userId = parseInt(useUserIdState());
  const { goWithFilter } = useDashboardCalendarFilter();

  const fromDate = moment().subtract(30, "days");
  const toDate = moment().subtract(1, "days");
  const period = { fromDate, toDate };
  const status: "DONE" | "IN_PROGRESS" | "LATE" = activitiesStatus.LATE;

  const getCalendarFilterProps = (type: string) => {
    return { onClick: () => goWithFilter({ status, type, period, userId }) };
  };

  return getCalendarFilterProps;
};

type ParsedLateActivitiesCounter = {
  type: CalendarActivityTypes;
  count: number;
}[];

const parseLateActivitiesCounterResponse = (
  response: LateActivitiesCounterResponse,
  dashboardModuleActivityTypes: CalendarActivityTypes[] = []
): ParsedLateActivitiesCounter => {
  const activityCounts = response?.reduce(
    (counts: { [x: string]: number }, bucket: { firstKey: string | number; count: number }) => {
      counts[bucket.firstKey] = bucket.count;
      return counts;
    },
    {}
  );

  return dashboardModuleActivityTypes.map((type) => ({ type, count: activityCounts?.[type] || 0 }));
};

const useLateActivitiesCounterQuery = (): [string, ParsedLateActivitiesCounter] => {
  const [lateActivitiesCount, setLateActivitiesCount] = useState<ParsedLateActivitiesCounter>([]);
  const [status, setStatus] = useState(QUERY_STATUS.loading);
  const DASHBOARD_MODULE_ACTIVITY_TYPES = useCalendarActivityTypes();
  const YESTERDAY = moment().subtract(1, "days").format("YYYY-MM-DD");
  const THIRTY_DAYS_AGO = moment().subtract(30, "days").format("YYYY-MM-DD");

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["lateActivityCounter", { from: THIRTY_DAYS_AGO, to: YESTERDAY }],
    () => perses.getLateActivitiesCount(THIRTY_DAYS_AGO, YESTERDAY).then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseLateActivitiesCounterResponse(data, DASHBOARD_MODULE_ACTIVITY_TYPES);
      setLateActivitiesCount(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [data, isSuccess, isLoading, isFetching, DASHBOARD_MODULE_ACTIVITY_TYPES]);

  return [status, lateActivitiesCount];
};

export default useLateActivitiesCounterQuery;
